import { checkCookieExists, getCookieValue, setCookie } from '@utils/web-storage';
import { recordMarketingSiteLD } from '@utils/launch-darkly';
import { userAgentIsBot } from '@utils/bot-detection';

const COOKIE_KEY = 'fundrise__mst';
const COOKIE_MAX_AGE = '2592000'; // 30 days in seconds

const TEST_VARIANTS_MAP: Record<string, string> = {
	'11-20-2025-hph-elevate-assets-test-v2': 'test-elevate-assets-copy'
};

enum MarketingSiteTestVariant {
	TEST,
	CONTROL
}

const generateVariant = () => (Math.random() >= 0.5 ? MarketingSiteTestVariant.TEST : MarketingSiteTestVariant.CONTROL);

const getTestVariant = (testName: string) => TEST_VARIANTS_MAP[testName] ?? 'test';

/**
 * This utility should be used for *UNAUTHENTICATED* A/B tests only that are *NOT* evaluated on the server
 *
 * To improve web performance, this utility exists to bypass LaunchDarkly and performance A/B test bucketing on
 * the client rather than the LaunchDarkly. A random variant is generated and stored in client cookies, and a
 * unique API endpoint is invoked to preserve analytics tracking.
 *
 * NOTES:
 *  - A *REAL* LaunchDarkly flag is required and must be *ENABLED*. The API still validates this before persisting to
 *    Dynamo.
 *  - The LaunchDarkly flag *MUST* have string variants. If the test variant is not "test", add it to TEST_VARIANTS_MAP
 *  - Do *NOT* create an experiment for the flag
 *
 *  - The API has a hardcoded list of supported LD flags for this endpoint - it will ONLY work for `11-20-2025-hph-elevate-assets-test-v2`
 *    at the moment. Any later test will be dependent on an API ticket to make their handling more generic.
 */
export const isInMarketingSiteTest = (flag: string) => {
	if (userAgentIsBot()) return false; // omit known bots

	try {
		const testConfig = JSON.parse(checkCookieExists(COOKIE_KEY) ? (getCookieValue(COOKIE_KEY) as string) : '{}');
		if (!Object.hasOwn(testConfig, flag)) {
			testConfig[flag] = generateVariant();

			// timeout to avoid delays of setting cookie and logging analytics by doing them in the background
			setTimeout(async () => {
				setCookie(COOKIE_KEY, JSON.stringify(testConfig), COOKIE_MAX_AGE);
				await recordMarketingSiteLD(
					flag,
					testConfig[flag] === MarketingSiteTestVariant.TEST ? getTestVariant(flag) : 'control'
				);
			});
		}
		return testConfig[flag] === MarketingSiteTestVariant.TEST;
	} catch {
		return false; // exclude from test enrollment and behavior if cookies are unavailable
	}
};
